import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'wouter';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, ChevronDown, ChevronRight, X } from 'lucide-react';

import { fetchCollection, formatPrice, productDisplayPrice, type ShopifyCollection, type ShopifyProduct } from '@/lib/shopifyApi';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CollectionSeoSection } from '@/components/sections/CollectionSeoSection';
import { SUPPORTING_COLLECTION_INTROS } from '@/data/supportingCollectionSeoContent';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import {
  CollectionFilters,
  type FilterState,
  EMPTY_FILTERS,
  hasActiveFilters,
  OKAZJE,
  KWIATY,
} from '@/components/collection/CollectionFilters';
import { findBreadcrumbParent } from '@/components/sections/megaMenuData';
import { collectionUrl, normalizeHandle } from '@/lib/collectionUrl';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ─── Constants ───────────────────────────────────────────── */
const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const LINE  = 'rgba(210,60,120,0.18)';
const MUTED = 'rgba(42,31,20,0.45)';
const CREAM  = '#FAF7F2';
const CREAM2 = '#F5EFE6';

/* ─── Local description overrides (take priority over Shopify) ── */
const COLLECTION_DESCRIPTIONS: Record<string, string> = {
  'bukiety':                   'Odkryj nasze bukiety kwiatów przygotowywane przez florystów Bukietowej w Lublinie. Znajdziesz tutaj świeże kompozycje kwiatowe na urodziny, rocznice, podziękowania, wyjątkowe okazje i spontaniczne gesty. Wybierz gotowy bukiet dopasowany do okazji lub postaw na kompozycję, która najlepiej odda Twoje emocje. Wszystkie bukiety możesz zamówić online z dostawą kwiatów na terenie Lublina i okolic.',
  'urodziny':                  'Świeże bukiety, flower boxy i gotowe kompozycje, które sprawią radość solenizantowi. Zamów kwiaty na urodziny z dostawą w Lublinie - tego samego dnia.',
  'imieniny':                  'Kwiaty na imieniny - bukiety i kompozycje kwiatowe z okazji dnia imienin. Zamów z dostawą w Lublinie.',
  'rocznica':                  'Romantyczne bukiety i kompozycje na rocznicę - wyraź uczucia pięknem kwiatów.',
  'przeprosiny':               'Kwiaty, które mówią więcej niż słowa. Wybierz bukiet na przeprosiny z dostawą w Lublinie.',
  'gratulacje':                'Wyjątkowe bukiety z gratulacjami - świetny prezent na każdy sukces i osiągnięcie.',
  'podziekowania':             'Kwiaty z podziękowaniem - piękny gest wdzięczności dla bliskich i współpracowników.',
  'niespodzianka':             'Zaskoczyj kogoś wyjątkową niespodzianką kwiatową. Dostawa tego samego dnia w Lublinie.',
  'dla-pary-mlodej':          'Bukiety i kompozycje ślubne dla Pary Młodej - elegancka oprawa najważniejszego dnia.',
  'komunia':                   'Delikatne kwiaty na Pierwszą Komunię Świętą. Białe i pastelowe kompozycje dla dzieci.',
  'obrona':                    'Kwiaty na obronę pracy dyplomowej - wyraź dumę i wsparcie pięknym bukietem.',
  'zareczyny':                 'Romantyczne kwiaty na zaręczyny - bukiety, które towarzyszą temu wyjątkowemu momentowi.',
  'dla-mamy':                  'Wyjątkowe kwiaty dla mamy - bo mama zasługuje na najpiękniejsze kompozycje.',
  'dla-zony':                  'Romantyczne bukiety dla żony - wyraź miłość i wdzięczność pięknem kwiatów.',
  'dla-dziewczyny':            'Kwiaty dla dziewczyny - romantyczne gesty i piękne bukiety z dostawą w Lublinie.',
  'dla-babci':                 'Ciepłe i piękne kwiaty dla babci - wdzięczność w każdym płatku.',
  'dla-mezczyzny':             'Eleganckie kompozycje kwiatowe dla mężczyzny - nowoczesne i efektowne.',
  'dla-przyjaciela':           'Kwiaty dla przyjaciela lub przyjaciółki - gest przyjaźni i życzliwości.',
  'walentynki':                'Romantyczne bukiety na Walentynki - róże, czerwone akcenty i miłosne kompozycje.',
  'dzien-kobiet':              'Kwiaty na Dzień Kobiet 8 marca - bo każda kobieta zasługuje na piękno.',
  'dzien-babci-i-dziadka':     'Ciepłe bukiety na Dzień Babci i Dzień Dziadka - wyraź miłość i wdzięczność.',
  'dzien-matki':               'Wyjątkowe kwiaty na Dzień Matki - najpiękniejszy prezent dla mamy.',
  'dzien-ojca':                'Kwiaty na Dzień Ojca - elegancki gest wdzięczności dla taty.',
  'dzien-dziecka':             'Kolorowe i radosne kwiaty na Dzień Dziecka - niespodzianka dla najmłodszych.',
  'boze-narodzenie':           'Świąteczne kompozycje kwiatowe na Boże Narodzenie - zimowe i pełne ciepła.',
  'wielkanoc':                 'Wiosenne kwiaty wielkanocne - tulipany, hiacynty i narcyzy na Wielkanoc.',
  'wszystkich-swietych':       'Spokojne i dostojne kompozycje na Wszystkich Świętych - kwiaty z szacunkiem.',
};

/* ─── H1 overrides for occasion / subcategory pages ─────────── */
const COLLECTION_H1_OVERRIDES: Record<string, string> = {
  'Urodziny':                  'Kwiaty na urodziny',
  'Imieniny':                  'Kwiaty na imieniny',
  'Rocznica':                  'Kwiaty na rocznicę',
  'Przeprosiny':               'Kwiaty na przeprosiny',
  'Gratulacje':                'Kwiaty z gratulacjami',
  'Podziękowania':             'Kwiaty z podziękowaniem',
  'Niespodzianka':             'Kwiaty w niespodziance',
  'Dla-pary-młodej':          'Kwiaty dla Pary Młodej',
  'Komunia':                   'Kwiaty na komunię',
  'Obrona':                    'Kwiaty na obronę',
  'Zaręczyny':                 'Kwiaty na zaręczyny',
  'Dla-mamy':                  'Kwiaty dla mamy',
  'Dla-żony':                  'Kwiaty dla żony',
  'Dla-dziewczyny':            'Kwiaty dla dziewczyny',
  'Dla-babci':                 'Kwiaty dla babci',
  'Dla-mężczyzny':             'Kwiaty dla mężczyzny',
  'Dla-przyjaciela':           'Kwiaty dla przyjaciela',
  'Walentynki':                'Kwiaty na Walentynki',
  'Dzień-kobiet':              'Kwiaty na Dzień Kobiet',
  'Dzień-babci-i-dziadka':    'Kwiaty na Dzień Babci',
  'Dzień-matki':               'Kwiaty na Dzień Matki',
  'Dzień-ojca':                'Kwiaty na Dzień Ojca',
  'Dzień-dziecka':             'Kwiaty na Dzień Dziecka',
  'Boże-narodzenie':           'Kwiaty na Boże Narodzenie',
  'Wielkanoc':                 'Kwiaty na Wielkanoc',
  'Wszystkich-świętych':       'Kwiaty na Wszystkich Świętych',
};

/* ─── Product-type chips for occasion pages ──────────────────── */
const OCCASION_PRODUCT_TYPES = [
  { label: 'Wszystko',           tag: null        },
  { label: 'Bukiety',            tag: 'bukiet'    },
  { label: 'Flower Boxy',        tag: 'flower'    },
  { label: 'Kwiaty doniczkowe',  tag: 'doniczkow' },
  { label: 'Kosze kwiatowe',     tag: 'kosz'      },
];

/* ─── All occasion page handles ─────────────────────────────── */
const OCCASION_HANDLES = new Set([
  // Na jaką okazję
  'Urodziny', 'Imieniny', 'Rocznica', 'Przeprosiny', 'Gratulacje',
  'Podziękowania', 'Niespodzianka', 'Dla-pary-młodej',
  'Komunia', 'Obrona', 'Zaręczyny',
  // Dla kogo
  'Dla-mamy', 'Dla-żony', 'Dla-dziewczyny', 'Dla-babci',
  'Dla-mężczyzny', 'Dla-przyjaciela',
  // Nadchodzące święta
  'Walentynki', 'Dzień-kobiet', 'Dzień-babci-i-dziadka',
  'Dzień-matki', 'Dzień-ojca', 'Dzień-dziecka',
  'Boże-narodzenie', 'Wielkanoc', 'Wszystkich-świętych',
]);

/* ─── Override: frontend handle → Shopify collection handle ─── */
const SHOPIFY_HANDLE_OVERRIDES: Record<string, string> = {
  // Occasion pages
  'Dzień-babci-i-dziadka': 'dzien-babci-i-dziadka',
  'Wszystkich-świętych':   'wszystkich-swietych',
  'Dla-pary-młodej':       'kwiaty-dla-pary-mlodej',
  // Product-type pages — Shopify handles differ from URL slugs
  'Flower-boxy':           'flowerbox',
  'Kosze-kwiatowe':        'kosze-kwiatowe',
  'Kwiaty-doniczkowe':     'kwiaty-doniczkowe-1',
};

/* ─── Editorial hero config per occasion handle ──────────────── */
type OkazjaConfig = {
  accent: string; h1Line1: string; h1Line2: string;
  description: string;
  cta1: { label: string; href: string };
  cta2: { label: string; href: string };
};
const CTA_DEFAULT = {
  cta1: { label: 'Stwórz bukiet', href: '/kreator/roze/' },
  cta2: { label: 'Cały sklep',    href: '/sklep/' },
};
const OCCASION_HERO_CONFIG: Record<string, OkazjaConfig> = {
  // ── Na jaką okazję ──
  'Urodziny':                { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'na urodziny',        description: 'Świeże bukiety, flower boxy i gotowe kompozycje, które sprawią radość solenizantowi. Zamów z dostawą w Lublinie - tego samego dnia.', ...CTA_DEFAULT },
  'Imieniny':                { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'na imieniny',        description: 'Piękne kwiaty z okazji imienin - tradycja i radość w jednym bukiecie. Dostawa w Lublinie.', ...CTA_DEFAULT },
  'Rocznica':                { accent: '#9B2335', h1Line1: 'Kwiaty', h1Line2: 'na rocznicę',        description: 'Romantyczne bukiety i kompozycje na rocznicę - wyraź uczucia pięknem świeżych kwiatów.', ...CTA_DEFAULT },
  'Przeprosiny':             { accent: '#7B8FA1', h1Line1: 'Kwiaty', h1Line2: 'na przeprosiny',     description: 'Kwiaty, które mówią więcej niż słowa. Wybierz bukiet na przeprosiny z dostawą w Lublinie.', ...CTA_DEFAULT },
  'Gratulacje':              { accent: '#e05590', h1Line1: 'Kwiaty', h1Line2: 'z gratulacjami',     description: 'Wyjątkowe bukiety na każdy sukces i osiągnięcie. Pogratuluj z klasą.', ...CTA_DEFAULT },
  'Podziękowania':           { accent: '#7A9E7E', h1Line1: 'Kwiaty', h1Line2: 'z podziękowaniem',   description: 'Piękny gest wdzięczności - kwiaty, które wyrażają szczere podziękowanie.', ...CTA_DEFAULT },
  'Niespodzianka':           { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'w niespodziance',    description: 'Zaskoczyj kogoś bliskiego wyjątkową niespodzianką kwiatową. Dostawa tego samego dnia.', ...CTA_DEFAULT },
  'Dla-pary-młodej':        { accent: '#9B2335', h1Line1: 'Kwiaty', h1Line2: 'dla Pary Młodej',    description: 'Eleganckie bukiety i kompozycje ślubne dla Pary Młodej - piękna oprawa najważniejszego dnia.', ...CTA_DEFAULT },
  'Komunia':                 { accent: '#A8C0D6', h1Line1: 'Kwiaty', h1Line2: 'na komunię',         description: 'Delikatne, białe i pastelowe kwiaty na Pierwszą Komunię Świętą.', ...CTA_DEFAULT },
  'Obrona':                  { accent: '#e05590', h1Line1: 'Kwiaty', h1Line2: 'na obronę',          description: 'Wyraź dumę i wsparcie - kwiaty na obronę pracy dyplomowej z dostawą w Lublinie.', ...CTA_DEFAULT },
  'Zaręczyny':               { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'na zaręczyny',       description: 'Romantyczne kwiaty towarzyszące temu wyjątkowemu momentowi - propozycji, która zostaje w pamięci.', ...CTA_DEFAULT },
  // ── Dla kogo ──
  'Dla-mamy':                { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'dla mamy',           description: 'Wyjątkowe kwiaty dla mamy - bo mama zasługuje na to, co najpiękniejsze.', ...CTA_DEFAULT },
  'Dla-żony':                { accent: '#9B2335', h1Line1: 'Kwiaty', h1Line2: 'dla żony',           description: 'Romantyczne bukiety dla żony - wyraź miłość i wdzięczność pięknem świeżych kwiatów.', ...CTA_DEFAULT },
  'Dla-dziewczyny':          { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'dla dziewczyny',     description: 'Romantyczne gesty i piękne bukiety dla dziewczyny z dostawą w Lublinie.', ...CTA_DEFAULT },
  'Dla-babci':               { accent: '#e05590', h1Line1: 'Kwiaty', h1Line2: 'dla babci',          description: 'Ciepłe i piękne kwiaty dla babci - wdzięczność i miłość w każdym płatku.', ...CTA_DEFAULT },
  'Dla-mężczyzny':           { accent: '#3B5378', h1Line1: 'Kwiaty', h1Line2: 'dla mężczyzny',      description: 'Eleganckie i efektowne kompozycje kwiatowe dla mężczyzny - nowoczesne i bez zbędnych ozdób.', ...CTA_DEFAULT },
  'Dla-przyjaciela':         { accent: '#7A9E7E', h1Line1: 'Kwiaty', h1Line2: 'dla przyjaciela',    description: 'Gest przyjaźni i życzliwości - kwiaty dla przyjaciela lub przyjaciółki na każdą chwilę.', ...CTA_DEFAULT },
  // ── Nadchodzące święta ──
  'Walentynki':              { accent: '#C0392B', h1Line1: 'Kwiaty', h1Line2: 'na Walentynki',      description: 'Romantyczne bukiety na Walentynki - czerwone róże, serca i miłosne kompozycje z dostawą.', ...CTA_DEFAULT },
  'Dzień-kobiet':            { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'na Dzień Kobiet',    description: 'Kwiaty na 8 marca - bo każda kobieta zasługuje na wyjątkowy bukiet.', ...CTA_DEFAULT },
  'Dzień-babci-i-dziadka':  { accent: '#e05590', h1Line1: 'Kwiaty', h1Line2: 'na Dzień Babci',     description: 'Ciepłe bukiety na Dzień Babci (21 stycznia) i Dzień Dziadka (22 stycznia) - wyraź miłość kwiatami.', ...CTA_DEFAULT },
  'Dzień-matki':             { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'na Dzień Matki',     description: 'Wyjątkowe kwiaty na Dzień Matki - najpiękniejszy prezent dla mamy w tym szczególnym dniu.', ...CTA_DEFAULT },
  'Dzień-ojca':              { accent: '#3B5378', h1Line1: 'Kwiaty', h1Line2: 'na Dzień Ojca',      description: 'Elegancki gest wdzięczności - kwiaty dla taty na jego wyjątkowy dzień.', ...CTA_DEFAULT },
  'Dzień-dziecka':           { accent: '#E08840', h1Line1: 'Kwiaty', h1Line2: 'na Dzień Dziecka',   description: 'Kolorowe i radosne kwiaty na Dzień Dziecka - niespodzianka dla najmłodszych.', ...CTA_DEFAULT },
  'Boże-narodzenie':         { accent: '#9B2335', h1Line1: 'Kwiaty', h1Line2: 'na Boże Narodzenie', description: 'Świąteczne kompozycje kwiatowe na Boże Narodzenie - zimowe, ciepłe, pełne świątecznego nastroju.', ...CTA_DEFAULT },
  'Wielkanoc':               { accent: '#7A9E7E', h1Line1: 'Kwiaty', h1Line2: 'na Wielkanoc',       description: 'Wiosenne kwiaty wielkanocne - tulipany, hiacynty i narcyzy na stół i w prezencie.', ...CTA_DEFAULT },
  'Wszystkich-świętych':     { accent: '#5A5A7A', h1Line1: 'Kwiaty', h1Line2: 'na Wszystkich Świętych', description: 'Spokojne i dostojne kompozycje kwiatowe na Wszystkich Świętych - z szacunkiem i pamięcią.', ...CTA_DEFAULT },
};

/* ─── Flower-type page handles (Typ A: primary=flower, secondary=product type) ─ */
const FLOWER_TYPE_HANDLES = new Set([
  'Róże', 'Lilie', 'Gerbery', 'Goździki', 'Słoneczniki',
  'Alstroemerie', 'Eustomy', 'Gipsówka', 'Margaretki',
]);

/* ─── Product-type page handles (Typ B: primary=product, secondary=flower) ───── */
const PRODUCT_TYPE_HANDLES = new Set([
  'Flower-boxy', 'Kosze-kwiatowe', 'Kwiaty-w-torebce',
  'Lasy-w-szkle', 'Balony-z-helem', 'Kwiaty-doniczkowe', 'Wieczne-róże',
]);

/* ─── Funeral subcategory handles ────────────────────────────────────────────── */
const FUNERAL_HANDLES = new Set([
  'Bukiety-pogrzebowe', 'Wieńce-pogrzebowe', 'Wiązanki-pogrzebowe', 'Sztuczne-kompozycje',
]);

/* ─── Badge label for product-type pages ────────────────────────────────────── */
const PRODUCT_TYPE_BADGE_LABELS: Record<string, string> = {
  'Flower-boxy':        'Flower Box',
  'Kosze-kwiatowe':     'Kwiaty w koszu',
  'Kwiaty-w-torebce':   'Kwiaty w torebce',
  'Lasy-w-szkle':       'Lasy w szkle',
  'Balony-z-helem':     'Balony z helem',
  'Kwiaty-doniczkowe':  'Kwiaty doniczkowe',
  'Wieczne-róże':       'Wieczne róże',
};

/* ─── Editorial hero config — flower-type pages (Typ A) ─────────────────────── */
const FLOWER_HERO_CONFIG: Record<string, OkazjaConfig> = {
  'Róże':         { accent: '#C0392B', h1Line1: 'Kompozycje', h1Line2: 'z róż',              description: 'Róże - symbol miłości, pasji i elegancji. W Bukietowej znajdziesz kompozycje z róż w każdym kolorze: bukiety, flower boxy, kosze i torebki. Zamów z dostawą w Lublinie.',      ...CTA_DEFAULT },
  'Lilie':        { accent: '#9B2335', h1Line1: 'Kompozycje', h1Line2: 'z lilii',             description: 'Dostojne i aromatyczne lilie w bukietach, flower boxach i kompozycjach kwiatowych. Idealne na każdą wyjątkową okazję. Dostawa w Lublinie.',                                      ...CTA_DEFAULT },
  'Gerbery':      { accent: '#E08840', h1Line1: 'Kompozycje', h1Line2: 'z gerberami',         description: 'Kolorowe i radosne gerbery w bukietach, flower boxach i koszach kwiatowych - idealne na urodziny, podziękowania i każdą wyjątkową chwilę. Dostawa w Lublinie.',                  ...CTA_DEFAULT },
  'Goździki':     { accent: '#D75B76', h1Line1: 'Kompozycje', h1Line2: 'z goździkami',        description: 'Eleganckie goździki w bukietach, flower boxach i kompozycjach kwiatowych - ponadczasowe kwiaty na każdą okazję i nastrój. Dostawa kwiatów w Lublinie.',                          ...CTA_DEFAULT },
  'Słoneczniki':  { accent: '#e05590', h1Line1: 'Kompozycje', h1Line2: 'ze słonecznikami',    description: 'Słoneczniki rozświetlają każdy dzień - ciepłe, radosne kompozycje w bukietach, koszach i flower boxach pełne energii. Zamów z dostawą w Lublinie.',                             ...CTA_DEFAULT },
  'Alstroemerie': { accent: '#7A9E7E', h1Line1: 'Kompozycje', h1Line2: 'z alstroemeriami',    description: 'Delikatne alstroemerie w bukietach, flower boxach i kompozycjach kwiatowych - trwałe, efektowne i wdzięczne. Dostawa w Lublinie.',                                              ...CTA_DEFAULT },
  'Eustomy':      { accent: '#A8C0D6', h1Line1: 'Kompozycje', h1Line2: 'z eustomami',         description: 'Eustomy - kwiaty o niepowtarzalnej delikatności w bukietach, flower boxach i eleganckich kompozycjach. Dostawa w Lublinie.',                                                    ...CTA_DEFAULT },
  'Gipsówka':     { accent: '#e05590', h1Line1: 'Kompozycje', h1Line2: 'z gipsówką',          description: 'Gipsówka jako baza lub główna dekoracja - tworzy lekkie, romantyczne i efektowne kompozycje w każdej formie. Dostawa w Lublinie.',                                              ...CTA_DEFAULT },
  'Margaretki':   { accent: '#7A9E7E', h1Line1: 'Kompozycje', h1Line2: 'z margaretkami',      description: 'Urocze i naturalne margaretki w bukietach, koszach i flower boxach - radość i lekkość w każdej kompozycji kwiatowej. Dostawa w Lublinie.',                                      ...CTA_DEFAULT },
};

/* ─── Editorial hero config — funeral subcategory pages ─────────────────────── */
const FUNERAL_HERO_CONFIG: Record<string, OkazjaConfig> = {
  'Bukiety-pogrzebowe':  { accent: '#4A6741', h1Line1: 'Bukiety', h1Line2: 'pogrzebowe',       description: 'Eleganckie kompozycje z białych i kremowych kwiatów - lilii, chryzantem i gerber. Przygotowywane ręcznie, z szacunkiem dla chwili. Dostawa w Lublinie.',             cta1: { label: 'Zadzwoń do nas',       href: 'tel:+48605030728' }, cta2: { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' } },
  'Wieńce-pogrzebowe':   { accent: '#4A6741', h1Line1: 'Wieńce', h1Line2: 'pogrzebowe',        description: 'Tradycyjne wieńce owalne i okrągłe z wstęgą lub bez. Dostępne w różnych rozmiarach, z możliwością umieszczenia napisu. Dostawa w Lublinie.',                          cta1: { label: 'Zadzwoń do nas',       href: 'tel:+48605030728' }, cta2: { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' } },
  'Wiązanki-pogrzebowe': { accent: '#4A6741', h1Line1: 'Wiązanki', h1Line2: 'pogrzebowe',      description: 'Delikatne wiązanki podkładane na trumnę lub układane na grobie. Komponowane ze świeżych kwiatów sezonowych. Dostawa w Lublinie.',                                    cta1: { label: 'Zadzwoń do nas',       href: 'tel:+48605030728' }, cta2: { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' } },
  'Sztuczne-kompozycje': { accent: '#4A6741', h1Line1: 'Sztuczne', h1Line2: 'kompozycje',      description: 'Trwałe kompozycje z kwiatów sztucznych - odporne na warunki atmosferyczne, wyglądają pięknie przez wiele lat. Idealne na grób jako długotrwałe upamiętnienie.',     cta1: { label: 'Zadzwoń do nas',       href: 'tel:+48605030728' }, cta2: { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' } },
};

/* ─── Editorial hero config — product-type pages (Typ B) ───────────────────── */
const PRODUCT_TYPE_HERO_CONFIG: Record<string, OkazjaConfig> = {
  'Flower-boxy':       { accent: '#e05590', h1Line1: 'Flower', h1Line2: 'Boxy',        description: 'Eleganckie flower boxy - kwiaty zapakowane w designerskie pudełka. Gotowy prezent kwiatowy dla każdego i na każdą okazję. Dostawa w Lublinie.',                            ...CTA_DEFAULT },
  'Kosze-kwiatowe':    { accent: '#7A9E7E', h1Line1: 'Kwiaty', h1Line2: 'w koszu',     description: 'Kwiaty w eleganckim koszu - wyjątkowy i efektowny prezent na urodziny, imieniny i każdą inną okazję. Dostawa w Lublinie.',                                                 ...CTA_DEFAULT },
  'Kwiaty-w-torebce':  { accent: '#D75B76', h1Line1: 'Kwiaty', h1Line2: 'w torebce',   description: 'Kwiaty w dekoracyjnej torebce - świeży, gotowy do wręczenia prezent kwiatowy na każdą okazję. Dostawa w Lublinie.',                                                         ...CTA_DEFAULT },
  'Lasy-w-szkle':      { accent: '#3B5378', h1Line1: 'Lasy', h1Line2: 'w szkle',       description: 'Miniaturowe ogrody zamknięte w szkle - trwała i wyjątkowa dekoracja do każdego wnętrza. Nie wymagają podlewania. Dostawa w Lublinie.',                                       ...CTA_DEFAULT },
  'Balony-z-helem':    { accent: '#E08840', h1Line1: 'Balony', h1Line2: 'z helem',     description: 'Balony z helem na urodziny, rocznice i każde świętowanie - sam balon lub w zestawie z kwiatami. Dostawa w Lublinie.',                                                        ...CTA_DEFAULT },
  'Kwiaty-doniczkowe': { accent: '#7A9E7E', h1Line1: 'Kwiaty', h1Line2: 'doniczkowe',  description: 'Trwałe kwiaty doniczkowe - piękna dekoracja i prezent, który cieszy długo po wręczeniu. Dostawa kwiatów doniczkowych w Lublinie.',                                          ...CTA_DEFAULT },
  'Wieczne-róże':      { accent: '#9B2335', h1Line1: 'Wieczne', h1Line2: 'róże',       description: 'Wieczne róże - zachowane w naturalnej formie, trwają lata bez pielęgnacji. Wyjątkowy prezent i nieprzemijająca dekoracja. Dostawa w Lublinie.',                              ...CTA_DEFAULT },
};

/* ─── Secondary-filter chip options ─────────────────────────────────────────── */
// Typ A (flower-type page) → secondary filter by product type, contextual per flower
const FLOWER_CHIPS_BASE = [
  { label: 'Wszystko',         tag: null      },
  { label: 'Bukiety',          tag: 'bukiet'  },
  { label: 'Flower Boxy',      tag: 'flower'  },
  { label: 'Kwiaty w koszu',   tag: 'kosz'    },
  { label: 'Kwiaty w torebce', tag: 'torebce' },
];

const FLOWER_CHIPS_BY_HANDLE: Record<string, typeof FLOWER_CHIPS_BASE> = {
  'Róże': [
    ...FLOWER_CHIPS_BASE,
    { label: 'Wieczne róże', tag: 'wieczne' },
  ],
};

// Returns contextual chips for the given flower handle (falls back to base)
const getFlowerChips = (h: string) => FLOWER_CHIPS_BY_HANDLE[h] ?? FLOWER_CHIPS_BASE;

// Typ B (product-type page) → secondary filter by flower type
const PRODUCT_TYPE_FLOWER_CHIPS = [
  { label: 'Wszystkie kwiaty',  tag: null              },
  { label: 'Róże',              tag: 'roze'            },
  { label: 'Lilie',             tag: 'lilie'           },
  { label: 'Gerbery',           tag: 'gerbery'         },
  { label: 'Goździki',          tag: 'gozdziki'        },
  { label: 'Słoneczniki',       tag: 'sloneczniki'     },
  { label: 'Alstroemerie',      tag: 'alstroemerie'    },
  { label: 'Eustomy',           tag: 'eustomy'         },
  { label: 'Gipsówka',          tag: 'gipsowka'        },
  { label: 'Margaretki',        tag: 'margaretki'      },
  { label: 'Mieszane',          tag: 'mieszan'         },
];

const SORT_OPTIONS = [
  { label: 'Polecane',       value: 'COLLECTION_DEFAULT', reverse: false },
  { label: 'Najpopularniejsze', value: 'BEST_SELLING',   reverse: false },
  { label: 'Cena ↑',         value: 'PRICE',             reverse: false },
  { label: 'Cena ↓',         value: 'PRICE',             reverse: true  },
  { label: 'Najnowsze',      value: 'CREATED',           reverse: true  },
];

/* ─── Sort dropdown ───────────────────────────────────────── */
function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => `${o.value}:${o.reverse}` === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '10px 20px', minHeight: 44,
          border: `1px solid ${open ? DARK : 'rgba(42,31,20,0.18)'}`,
          background: open ? DARK : '#fff',
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          fontWeight: 600, color: open ? '#fff' : 'rgba(42,31,20,0.6)',
          cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: open ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
        }}
      >
        <SlidersHorizontal size={13} color={open ? 'rgba(255,255,255,0.7)' : GOLD} />
        {current.label}
        <ChevronDown size={11} color={open ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${LINE}`,
          minWidth: 200, boxShadow: '0 12px 40px rgba(42,31,20,0.1)',
        }}>
          {SORT_OPTIONS.map(o => {
            const key = `${o.value}:${o.reverse}`;
            const active = value === key;
            return (
              <button
                key={key}
                onClick={() => { onChange(key); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 16px',
                  background: active ? 'rgba(210,60,120,0.07)' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                  color: active ? DARK : 'rgba(42,31,20,0.7)',
                  fontWeight: active ? 600 : 400,
                  borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton ────────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="col-grid col-skeleton" style={{
      display: 'grid',
      gap: 'clamp(12px, 1.5vw, 24px)',
    }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="col-skeleton-item">
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)', marginBottom: 0 }} className="animate-pulse" />
          <div style={{ padding: '20px 22px 24px', background: '#fff' }}>
            <div style={{ height: 20, background: 'rgba(210,60,120,0.07)', marginBottom: 10, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 6, width: '90%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 14, width: '60%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%' }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter logic ────────────────────────────────────────── */
function normalize(s: string) {
  return s.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // strip diacritics
    .replace(/\s+/g, '-');
}

// Tags are always empty in the Shopify response — all filtering uses collection handles.
function applyFilters(products: ShopifyProduct[], filters: FilterState, maxPrice: number): ShopifyProduct[] {
  return products.filter(p => {
    const price = parseFloat(p.priceRange.minVariantPrice.amount);
    const cols  = new Set((p.collections?.nodes ?? []).map(c => c.handle));

    if (filters.okazje.length > 0) {
      const ok = filters.okazje.some(o => {
        const h = OKAZJE.find(x => x.value === o)?.handle;
        return h ? cols.has(h) : false;
      });
      if (!ok) return false;
    }

    if (filters.kwiaty.length > 0) {
      const ok = filters.kwiaty.some(k => {
        const h = KWIATY.find(x => x.value === k)?.handle;
        return h ? cols.has(h) : false;
      });
      if (!ok) return false;
    }

    if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;

    return true;
  });
}

/* ─── Normalize Polish URL slug → Shopify handle (lowercase ASCII) ── */
function toShopifyHandle(slug: string): string {
  return slug
    .toLowerCase()
    .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
    .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
    .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z');
}

/* ─── Fallback breadcrumb parents for pages NOT in mega menu ── */
const COLLECTION_PARENTS: Record<string, { label: string; href?: string }> = {
  'bestsellery':        { label: 'Sklep',   href: '/sklep/' },
  'bukiety':            { label: 'Sklep',   href: '/sklep/' },
  'bukiety-pogrzebowe': { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
  'wience':             { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
  'wiazanki':           { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
  'sztuczne-kompozycje':{ label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
  'wience-pogrzebowe':  { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
  'wiazanki-pogrzebowe':{ label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' },
};

/* ─── Page ────────────────────────────────────────────────── */
export default function CollectionPage({ fixedHandle }: { fixedHandle?: string } = {}) {
  const params = useParams<{ handle: string }>();
  const handle = fixedHandle ?? params.handle ?? '';
  // A new URL must not inherit the previous collection's title or late response.
  return <CollectionPageContent key={handle} handle={handle} />;
}

function CollectionPageContent({ handle }: { handle: string }) {
  const [collection, setCollection] = useState<ShopifyCollection | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  usePrerenderReady(!loading, notFound ? 404 : 200);
  const [sort,        setSort]       = useState('COLLECTION_DEFAULT:false');

  const collectionSeoDescription = collection
    ? (COLLECTION_DESCRIPTIONS[handle.toLowerCase()] ?? collection.description)
      || `Odkryj ${collection.title} w Bukietowej — zamów z dostawą na terenie Lublina i okolic.`
    : undefined;

  useSeo({
    title: collection?.title ?? handle,
    description: collectionSeoDescription,
    ogImage: (collection?.image as { url?: string } | null | undefined)?.url,
  });
  const [filters,     setFilters]   = useState<FilterState>(EMPTY_FILTERS);
  const [productType, setProductType] = useState<string | null>(null);
  const [priceOpen,   setPriceOpen]  = useState(false);
  const isOccasionPage    = OCCASION_HANDLES.has(handle);
  const isFlowerPage      = FLOWER_TYPE_HANDLES.has(handle);
  const isProductTypePage = PRODUCT_TYPE_HANDLES.has(handle);
  const isFuneralPage     = FUNERAL_HANDLES.has(handle);
  const isEditorialPage   = isOccasionPage || isFlowerPage || isProductTypePage || isFuneralPage;

  useEffect(() => {
    if (!handle) return;
    setLoading(true); setNotFound(false); setFilters(EMPTY_FILTERS); setProductType(null);
    const [sortKey, reverseStr] = sort.split(':');
    const shopifyHandle = SHOPIFY_HANDLE_OVERRIDES[handle] ?? toShopifyHandle(handle);
    fetchCollection(shopifyHandle, { sort: sortKey, reverse: reverseStr === 'true', limit: 100 })
      .then(col => { if (!col) setNotFound(true); else setCollection(col); })
      .catch(err => {
        // Preserve the previous collection while a refresh is unavailable.
        console.warn(`[CollectionPage] refresh failed for ${shopifyHandle}:`, err);
      })
      .finally(() => setLoading(false));
  }, [handle, sort]);

  const allProducts = collection?.products.nodes ?? [];

  const maxPrice = useMemo(() => {
    if (allProducts.length === 0) return 1000;
    const m = Math.max(...allProducts.map(p => parseFloat(p.priceRange.minVariantPrice.amount)));
    return Math.ceil(m / 50) * 50; // round up to nearest 50
  }, [allProducts]);

  // Sync maxPrice into filter when data loads
  useEffect(() => {
    if (maxPrice > 0) {
      setFilters(f => ({
        ...f,
        priceRange: [f.priceRange[0], Math.max(f.priceRange[1], maxPrice)],
      }));
    }
  }, [maxPrice]);

  const filtered = useMemo(() => {
    const base = applyFilters(allProducts, filters, maxPrice);
    if (!productType) return base;
    return base.filter(p => p.tags.map(normalize).some(t => t.includes(productType)));
  }, [allProducts, filters, maxPrice, productType]);

  const activeFilters = hasActiveFilters(filters, maxPrice);

  const availableOkazje = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return OKAZJE.filter(o => o.handle && handles.has(o.handle));
  }, [allProducts]);

  const availableKwiaty = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return KWIATY.filter(k => k.handle && handles.has(k.handle));
  }, [allProducts]);

  // Derive parent from menu structure first; fall back to manual map for non-menu pages
  const parent = findBreadcrumbParent(handle) ?? COLLECTION_PARENTS[normalizeHandle(handle)];
  const breadcrumbs = [
    ...(parent ? [parent] : []),
    { label: collection?.title ?? handle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') },
  ];

  return (
    <>
      <div style={{ background: '#fff', minHeight: '100vh' }}>
        <PageBreadcrumb crumbs={breadcrumbs} />

        {/* ── Hero header ── */}
        {isEditorialPage ? (() => {
          const cfg = OCCASION_HERO_CONFIG[handle] ?? FLOWER_HERO_CONFIG[handle] ?? PRODUCT_TYPE_HERO_CONFIG[handle] ?? FUNERAL_HERO_CONFIG[handle];
          if (!cfg) return null;
          const mosaicImgs = allProducts
            .map(p => (p.images?.nodes ?? [])[0])
            .filter(i => i?.url)
            .slice(0, 4);
          return (
            <section style={{
              background: '#fff',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                maxWidth: 1370, margin: '0 auto',
                padding: 'max(clamp(48px,6vw,88px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(40px,5vw,64px)',
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: 'clamp(32px,5vw,72px)', alignItems: 'center',
              }} className="col-okazja-hero">

                {/* Left — text */}
                <div>
                  <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, marginBottom: 16 }}>
                    Bukietowa · Lublin
                  </motion.p>

                  <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.06 }}
                    style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(42px,6vw,76px)', fontWeight: 600, lineHeight: 1.08, color: DARK, margin: '0 0 20px', letterSpacing: '-0.01em' }}>
                    <CmsEditorialText id="category.heading" value={cfg.h1Line1} section="Wstęp" label="Nagłówek" /><br />
                    <span style={{ color: cfg.accent }}><CmsEditorialText id="category.accent" value={cfg.h1Line2} section="Wstęp" label="Wyróżniona część nagłówka" /></span>
                  </motion.h1>

                  <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, lineHeight: 1.7, color: MUTED, maxWidth: 440, marginBottom: 32 }}>
                    <CmsEditorialText id="category.description" value={cfg.description} section="Wstęp" label="Opis kategorii" multiline />
                  </motion.p>

                  {!loading && !notFound && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, marginTop: 24 }}>
                      {allProducts.length} produktów w ofercie
                    </motion.p>
                  )}
                </div>

                {/* Right — 2×2 mosaic */}
                {mosaicImgs.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {mosaicImgs.map((img, i) => (
                      <div key={i} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', aspectRatio: '4/5' }}>
                        <img src={img!.url} alt={img!.altText ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>
            </section>
          );
        })() : (
          /* ── Standard hero (non-occasion collections) ── */
          <div style={{ background: '#fff', overflow: 'hidden' }}>
            <div className="col-hero" style={{ maxWidth: 1370, margin: '0 auto' }}>

              {/* Left: editorial text */}
              <div style={{
                padding: 'max(clamp(36px,4vw,60px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(36px,4vw,60px)',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                <div>
                  <motion.div
                    initial={{ scaleX: 0, originX: 0 }} animate={{ scaleX: 1 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    style={{ width: 52, height: 1.5, background: GOLD, marginBottom: 24 }}
                  />
                  <motion.h1
                    initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
                    style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: 'clamp(44px, 5.5vw, 80px)',
                      fontWeight: 300, color: DARK, lineHeight: 1.0,
                      margin: '0 0 20px', letterSpacing: '-0.01em',
                    }}
                  >
                    {loading ? '\u00A0' : <CmsEditorialText id="category.heading" value={SUPPORTING_COLLECTION_INTROS[handle]?.h1 ?? COLLECTION_H1_OVERRIDES[handle] ?? collection?.title ?? handle} section="Wstęp" label="Nagłówek" />}
                  </motion.h1>
                  {(SUPPORTING_COLLECTION_INTROS[handle]?.lead ?? COLLECTION_DESCRIPTIONS[toShopifyHandle(handle)] ?? collection?.description) && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.18 }}
                      style={{
                        fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic',
                        fontSize: 'clamp(16px, 1.5vw, 20px)',
                        color: 'rgba(42,31,20,0.55)', lineHeight: 1.6, margin: 0, maxWidth: 480,
                      }}
                    >
                      <CmsEditorialText id="category.description" value={SUPPORTING_COLLECTION_INTROS[handle]?.lead ?? COLLECTION_DESCRIPTIONS[toShopifyHandle(handle)] ?? collection?.description ?? ''} section="Wstęp" label="Opis kategorii" multiline />
                    </motion.p>
                  )}
                </div>
                {!loading && !notFound && (
                  <motion.p
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                      letterSpacing: '0.14em', color: MUTED,
                      textTransform: 'uppercase', margin: 0, marginTop: 28,
                    }}
                  >
                    {activeFilters
                      ? `${filtered.length} z ${allProducts.length} produktów`
                      : `${allProducts.length} ${
                          allProducts.length === 1 ? 'produkt'
                          : allProducts.length < 5 ? 'produkty'
                          : 'produktów'
                        }`}
                  </motion.p>
                )}
              </div>

              {/* Right: fanned overlapping photos */}
              {(() => {
                const imgs = allProducts
                  .map(p => (p.images?.nodes ?? [])[0])
                  .filter(i => i?.url)
                  .slice(0, 3);
                const [a, b, c] = imgs;
                if (!a && !b && !c) return null;
                const cards = [
                  { img: a, rotate: '-7deg', z: 1, delay: 0.15 },
                  { img: b, rotate:  '2deg', z: 2, delay: 0.25 },
                  { img: c, rotate:  '9deg', z: 3, delay: 0.35 },
                ];
                const W = 210, H = 290;
                return (
                  <div className="col-hero-fan" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    paddingRight: 'clamp(32px,5vw,80px)',
                  }}>
                    <div style={{ position: 'relative', width: W + 2 * (W - 60), height: H + 40 }}>
                      {cards.map((card, i) => card.img && (
                        <motion.div key={i}
                          initial={{ opacity: 0, y: 30, rotate: card.rotate }}
                          animate={{ opacity: 1, y: 0, rotate: card.rotate }}
                          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: card.delay }}
                          style={{
                            position: 'absolute',
                            top: i === 1 ? 0 : i === 0 ? 20 : 14,
                            left: i * (W - 60),
                            width: W, height: H, zIndex: card.z,
                            boxShadow: '0 12px 40px rgba(42,31,20,0.22)',
                            overflow: 'hidden', transformOrigin: 'bottom center',
                          }}
                        >
                          <img src={card.img.url} alt={card.img.altText ?? ''}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Editorial chip bar — occasion / flower-type / product-type pages ── */}
        {isEditorialPage && !loading && (() => {
          const handleLabel = (h: string) => h.replace(/-/g, ' ');
          const badgeText = isOccasionPage
            ? `Okazja: ${handleLabel(handle)}`
            : isFlowerPage
              ? `Rodzaj kwiatu: ${handleLabel(handle)}`
              : isFuneralPage
                ? handleLabel(handle)
                : `Rodzaj produktu: ${PRODUCT_TYPE_BADGE_LABELS[handle] ?? handleLabel(handle)}`;
          const chips = isOccasionPage
            ? OCCASION_PRODUCT_TYPES
            : isFlowerPage
              ? getFlowerChips(handle)
              : isFuneralPage
                ? [{ label: 'Wszystkie', tag: null }]
                : PRODUCT_TYPE_FLOWER_CHIPS;
          const priceActive = filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice;
          const priceLabel  = priceActive ? `${filters.priceRange[0]}–${filters.priceRange[1]} zł` : 'Cena';

          return (
            <div className="page-container">
              <div style={{
                background: '#fff',
                borderTop: `1px solid rgba(210,60,120,0.18)`,
                borderBottom: `1px solid rgba(210,60,120,0.18)`,
              }}>
                {/* ── outer row: left-group flex:1  +  POLECANE on far right ── */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0' }}>

                  {/* ── Left group: badge | chips | CENA ── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>

                    {/* Context badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center',
                      padding: '10px 16px',
                      background: GOLD + '18', border: `1px solid ${GOLD}`,
                      fontFamily: 'DM Sans, sans-serif', fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.13em', textTransform: 'uppercase', color: DARK,
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                      {badgeText}
                    </span>

                    <span style={{ width: 1, height: 18, background: LINE, flexShrink: 0 }} />

                    {/* Scrollable product-type chips */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', flex: 1, minWidth: 0 }}>
                      {chips.map(opt => {
                        const active = productType === opt.tag;
                        return (
                          <button
                            key={opt.label}
                            onClick={() => setProductType(opt.tag)}
                            style={{
                              flexShrink: 0, padding: '10px 20px', minHeight: 44,
                              border: `1px solid ${active ? GOLD : 'rgba(42,31,20,0.18)'}`,
                              background: active ? GOLD : 'transparent',
                              color: active ? '#fff' : 'rgba(42,31,20,0.6)',
                              fontFamily: 'DM Sans, sans-serif', fontSize: 10,
                              letterSpacing: '0.13em', textTransform: 'uppercase',
                              fontWeight: 600, cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              boxShadow: active ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
                              transition: 'all 0.18s',
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* ── Compact price dropdown (inside left group, outside scrollable) ── */}
                    {maxPrice > 0 && <span style={{ width: 1, height: 18, background: LINE, flexShrink: 0 }} />}
                    {maxPrice > 0 && (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        onClick={() => setPriceOpen(o => !o)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 7,
                          padding: '10px 20px', minHeight: 44,
                          border: `1px solid ${priceActive || priceOpen ? GOLD : 'rgba(42,31,20,0.18)'}`,
                          background: priceOpen ? DARK : priceActive ? GOLD : 'transparent',
                          color: priceOpen ? '#fff' : priceActive ? '#fff' : 'rgba(42,31,20,0.6)',
                          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
                          letterSpacing: '0.13em', textTransform: 'uppercase', fontWeight: 600,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                          boxShadow: priceOpen || priceActive ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
                          transition: 'all 0.18s',
                        }}
                      >
                        {priceLabel}
                        <ChevronDown
                          size={11}
                          color={priceOpen ? 'rgba(255,255,255,0.7)' : priceActive ? GOLD : 'rgba(42,31,20,0.35)'}
                          style={{ transform: priceOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                        />
                      </button>

                      <AnimatePresence>
                        {priceOpen && (
                          <>
                            {/* Click-away backdrop */}
                            <motion.div
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              onClick={() => setPriceOpen(false)}
                              style={{ position: 'fixed', inset: 0, zIndex: 39 }}
                            />
                            {/* Dropdown panel */}
                            <motion.div
                              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.16 }}
                              style={{
                                position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
                                background: '#fff',
                                border: `1px solid ${LINE}`,
                                boxShadow: '0 12px 40px rgba(42,31,20,0.12)',
                                padding: '20px 22px',
                                minWidth: 260,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED }}>
                                  Zakres ceny
                                </span>
                                {priceActive && (
                                  <button
                                    onClick={() => setFilters(f => ({ ...f, priceRange: [0, maxPrice] }))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED }}
                                  >
                                    <X size={10} /> Reset
                                  </button>
                                )}
                              </div>

                              {/* Od/Do inputs */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED }}>Od</span>
                                  <div style={{ position: 'relative' }}>
                                    <input
                                      type="number" min={0} max={filters.priceRange[1] - 1}
                                      value={filters.priceRange[0]}
                                      onChange={e => setFilters(f => ({ ...f, priceRange: [Math.max(0, Math.min(+e.target.value, f.priceRange[1] - 1)), f.priceRange[1]] }))}
                                      style={{ width: '100%', padding: '10px 32px 10px 12px', border: `1px solid rgba(42,31,20,0.18)`, background: '#fff', fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: DARK, outline: 'none' }}
                                    />
                                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, pointerEvents: 'none' }}>zł</span>
                                  </div>
                                </div>

                                <div style={{ width: 16, height: 1, background: 'rgba(42,31,20,0.2)', marginTop: 18, flexShrink: 0 }} />

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED }}>Do</span>
                                  <div style={{ position: 'relative' }}>
                                    <input
                                      type="number" min={filters.priceRange[0] + 1} max={maxPrice}
                                      value={filters.priceRange[1]}
                                      onChange={e => setFilters(f => ({ ...f, priceRange: [f.priceRange[0], Math.max(f.priceRange[0] + 1, Math.min(+e.target.value, maxPrice))] }))}
                                      style={{ width: '100%', padding: '10px 32px 10px 12px', border: `1px solid rgba(42,31,20,0.18)`, background: '#fff', fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: DARK, outline: 'none' }}
                                    />
                                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, pointerEvents: 'none' }}>zł</span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                    )}
                  </div>{/* ── end left group ── */}

                  {/* ── POLECANE: alone on far right ── */}
                  <span style={{ width: 1, height: 18, background: LINE, flexShrink: 0, marginLeft: 8 }} />
                  <SortDropdown value={sort} onChange={setSort} />
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── White section: filters + product grid ── */}
        <div className="page-container" style={{ background: '#ffffff' }}>

        {/* ── Standard filter bar (non-occasion or when products exist) ── */}
        {!loading && !notFound && allProducts.length > 0 && !isEditorialPage && (
          <div style={{
            background: '#ffffff',
            borderTop: `1px solid rgba(210,60,120,0.18)`,
            borderBottom: `1px solid rgba(210,60,120,0.18)`,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CollectionFilters
                    filters={filters}
                    onChange={setFilters}
                    maxPrice={maxPrice}
                    sortValue={sort}
                    onSortChange={setSort}
                    sortOptions={SORT_OPTIONS}
                    okazjeOptions={availableOkazje.length > 0 ? availableOkazje : undefined}
                    kwiatyOptions={availableKwiaty.length > 0 ? availableKwiaty : undefined}
                  />
                </div>
                <div className="col-sort-desktop" style={{ flexShrink: 0, paddingTop: 1 }}>
                  <SortDropdown value={sort} onChange={setSort} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Product grid ── */}
        <div style={{
          padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)',
        }}>

          {loading && <SkeletonGrid />}

          {!loading && notFound && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 16 }}>
                Kolekcja nie istnieje
              </p>
              <Link href="/" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', color: GOLD, textDecoration: 'none', textTransform: 'uppercase' }}>
                Wróć na stronę główną →
              </Link>
            </div>
          )}

          {!loading && !notFound && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 8 }}>
                {activeFilters ? 'Brak produktów spełniających filtry' : 'Brak produktów'}
              </p>
              {activeFilters && (
                <button
                  onClick={() => setFilters({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] })}
                  style={{
                    fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                    color: GOLD, background: 'none', border: 'none', cursor: 'pointer',
                    textTransform: 'uppercase', textDecoration: 'underline',
                  }}
                >
                  Wyczyść filtry
                </button>
              )}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="col-grid" style={{
              display: 'grid',
              gap: 'clamp(12px, 1.5vw, 24px)',
            }}>
              {filtered.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={{ label: collection?.title ?? handle, href: collectionUrl(handle) }}
                  centerInfo
                />
              ))}
            </div>
          )}
        </div>
        </div>{/* end white section */}
      </div>

      <CollectionSeoSection handle={handle} />
      <Footer />

      <style>{`
        /* ── Product grid responsive columns ── */
        .col-grid { grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 768px) {
          .col-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }

        /* ── Skeleton: hide excess items at each breakpoint ── */
        .col-skeleton-item:nth-child(n+6) { display: block; }
        @media (max-width: 1200px) {
          .col-skeleton-item:nth-child(n+9) { display: none; }
        }
        @media (max-width: 768px) {
          .col-skeleton-item:nth-child(n+5) { display: none; }
        }


        /* ── Hero: responsive two-column → single column ── */
        .col-hero {
          display: grid;
          grid-template-columns: 1fr clamp(260px, 38vw, 560px);
          min-height: clamp(320px, 40vw, 480px);
        }
        @media (max-width: 767px) {
          .col-hero {
            grid-template-columns: 1fr;
            min-height: unset;
          }
          .col-hero-fan {
            padding: clamp(24px, 5vw, 48px) clamp(24px, 5vw, 48px) clamp(32px, 5vw, 48px) !important;
            padding-right: clamp(24px, 5vw, 48px) !important;
            max-height: 260px;
            overflow: hidden;
          }
          .col-hero-fan > div {
            transform: scale(0.72);
            transform-origin: top center;
          }
        }
      `}</style>
    </>
  );
}
